<template>
  <label
    ref="labelRef"
    :for="`image-upload-${block.id}`"
    class="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-300 cursor-pointer"
    :class="wrapperClasses"
  >
    <div class="absolute top-0 right-0 z-10 editable-image flex gap-2" :class="buttonClasses">
      <button
        type="button"
        @click.prevent.stop="startReframing"
        :disabled="isSaving"
        class="relative inline-flex items-center justify-center text-sm leading-4 font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 group text-white focus:ring-indigo-500 p-3 rounded-xl w-fit"
        :class="{
          'cursor-not-allowed bg-indigo-600 hover:bg-indigo-400': isSaving,
          'cursor-pointer bg-indigo-600 hover:bg-indigo-700': !isSaving
        }"
        :title="t('slides_editor.components.image_upload_overlay.reframe')"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-6 w-6 mr-2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
        </svg>
        {{ t('slides_editor.components.image_upload_overlay.reframe') }}
      </button>

      <button
        v-if="block.pictures_path"
        type="button"
        @click.prevent.stop="isChoosing = true"
        :disabled="isSaving"
        class="relative inline-flex items-center justify-center text-sm leading-4 font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 group text-white focus:ring-indigo-500 p-3 rounded-xl w-fit cursor-pointer bg-indigo-600 hover:bg-indigo-700"
        :title="t('slides_editor.components.picture_choice.choose')"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-6 w-6 mr-2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
        </svg>
        {{ t('slides_editor.components.picture_choice.choose') }}
      </button>

      <div
        class="relative inline-flex items-center justify-center text-sm leading-4 font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 group text-white focus:ring-indigo-500 p-3 rounded-xl w-fit"
        :class="{
          'cursor-not-allowed bg-indigo-600 hover:bg-indigo-400': isSaving,
          'cursor-pointer bg-indigo-600 hover:bg-indigo-700': !isSaving
        }"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-6 w-6 mr-2">
          <path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
        </svg>
        {{ isSaving ? t('slides_editor.components.image_upload_overlay.updating') : t('slides_editor.components.image_upload_overlay.update') }}
      </div>
      <input
        :id="`image-upload-${block.id}`"
        type="file"
        accept="image/*"
        class="hidden"
        @change="handleImageUpdate"
        :disabled="isSaving"
      />
    </div>
    <div class="w-full h-full rounded-md z-20" data-editable-image></div>
  </label>

  <Teleport to="body">
    <PictureChoice
      v-if="isChoosing"
      :pictures-path="block.pictures_path"
      @close="isChoosing = false"
    />
  </Teleport>

  <Teleport to="body">
    <ImageReframer
      v-if="isReframing"
      :key="block.image_url"
      :block="block"
      :aspect-ratio="aspectRatio"
      @close="isReframing = false"
    />
  </Teleport>
</template>

<script>
import { inject, nextTick, ref } from 'vue'
import { useBlockUpdate } from '../../composables/useBlockUpdate'
import { t } from '@/libs/i18n'
import ImageReframer from './ImageReframer.vue'
import PictureChoice from './PictureChoice.vue'

export default {
  name: 'ImageUploadOverlay',
  components: {
    ImageReframer,
    PictureChoice
  },
  props: {
    block: {
      type: Object,
      required: true
    },
    wrapperClasses: {
      type: String,
      default: ''
    },
    buttonClasses: {
      type: String,
      default: 'pr-12 pt-12'
    }
  },
  setup(props) {
    const isSaving = inject('isSaving')
    const { onBlockUpdate } = useBlockUpdate(props.block)
    const isReframing = ref(false)
    const isChoosing = ref(false)
    const aspectRatio = ref(1)
    const labelRef = ref(null)

    const measureAspect = () => {
      const parent = labelRef.value?.parentElement
      if (!parent) return
      const rect = parent.getBoundingClientRect()
      if (rect.width > 0 && rect.height > 0) {
        aspectRatio.value = rect.width / rect.height
      }
    }

    const startReframing = () => {
      measureAspect()
      isReframing.value = true
    }

    const handleImageUpdate = async (event) => {
      const file = event.target.files[0]
      if (!file) return
      const result = await onBlockUpdate(file, 'image')
      if (result) {
        await nextTick()
        startReframing()
      }
    }

    return {
      labelRef,
      isSaving,
      isReframing,
      isChoosing,
      aspectRatio,
      handleImageUpdate,
      startReframing,
      t
    }
  }
}
</script>
